import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Check, Volume2, Ear, Brain, Trash2, Save } from "lucide-react";
import { useState, useEffect } from "react";
import { BACKGROUNDS, PERSONALITIES } from "@/lib/constants";

export const SettingsPanel = ({ open, onOpenChange, prefs, onChange, memory, onSaveProfile, onDeleteMemory }) => {
  const [profileDraft, setProfileDraft] = useState("");
  useEffect(() => { setProfileDraft(memory?.profile || ""); }, [memory?.profile, open]);

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid="settings-panel"
      className="bg-slate-950/95 border border-cyan-500/20 backdrop-blur-2xl text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto"
    >
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl font-light text-glow">
          Personalización de Kai
        </DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">
          Ajusta el fondo, la personalidad, la voz y la memoria de tu asistente.
        </DialogDescription>
      </DialogHeader>

      {/* Background */}
      <section className="mt-2">
        <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-400/80 mb-3">
          Fondo de la conversación
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {BACKGROUNDS.map((bg) => (
            <button
              key={bg.id}
              data-testid={`bg-${bg.id}`}
              onClick={() => onChange({ background: bg.id })}
              className={`relative h-16 rounded-xl border transition-all overflow-hidden ${
                prefs.background === bg.id
                  ? "border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.4)]"
                  : "border-white/10 hover:border-white/30"
              }`}
              style={{ background: bg.style }}
            >
              {prefs.background === bg.id && (
                <Check className="absolute top-1.5 right-1.5 w-4 h-4 text-cyan-300" />
              )}
              <span className="absolute bottom-1 left-2 text-[10px] font-mono text-slate-300">
                {bg.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Personality */}
      <section className="mt-6">
        <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-400/80 mb-3">
          Personalidad
        </h3>
        <div className="space-y-2">
          {PERSONALITIES.map((p) => (
            <button
              key={p.id}
              data-testid={`personality-${p.id}`}
              onClick={() => onChange({ personality: p.id })}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                prefs.personality === p.id
                  ? "border-cyan-400 bg-cyan-500/10"
                  : "border-white/10 hover:bg-white/5"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-100">{p.name}</span>
                {prefs.personality === p.id && <Check className="w-4 h-4 text-cyan-300" />}
              </div>
              <span className="text-xs text-slate-400">{p.desc}</span>
            </button>
          ))}
        </div>
        {prefs.personality === "custom" && (
          <Textarea
            data-testid="custom-personality-input"
            value={prefs.custom_personality || ""}
            onChange={(e) => onChange({ custom_personality: e.target.value })}
            placeholder="Describe cómo quieres que sea Kai (tono, carácter, forma de hablar...)"
            className="mt-3 bg-black/40 border-white/10 text-slate-200 min-h-[90px]"
          />
        )}
      </section>

      {/* Voice */}
      <section className="mt-6 space-y-4">
        <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-400/80">
          Voz
        </h3>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-slate-300">
            <Volume2 className="w-4 h-4 text-cyan-400" /> Respuestas habladas (voz masculina)
          </span>
          <Switch
            data-testid="voice-pref-toggle"
            checked={prefs.voice_enabled}
            onCheckedChange={(v) => onChange({ voice_enabled: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-slate-300">
            <Ear className="w-4 h-4 text-cyan-400" /> Escucha continua (manos libres)
          </span>
          <Switch
            data-testid="listen-pref-toggle"
            checked={prefs.continuous_listening}
            onCheckedChange={(v) => onChange({ continuous_listening: v })}
          />
        </div>
      </section>

      {/* Memory & profile */}
      <section className="mt-6">
        <h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-cyan-400/80 mb-3 flex items-center gap-2">
          <Brain className="w-3.5 h-3.5" /> Memoria y perfil
        </h3>
        <p className="text-xs text-slate-400 mb-2">
          Información permanente que Kai tendrá siempre en cuenta sobre ti.
        </p>
        <Textarea
          data-testid="profile-input"
          value={profileDraft}
          onChange={(e) => setProfileDraft(e.target.value)}
          placeholder="Cuéntale a Kai quién eres, cómo quieres que te trate, tus objetivos..."
          className="bg-black/40 border-white/10 text-slate-200 min-h-[110px] text-sm"
        />
        <button
          data-testid="save-profile-btn"
          onClick={() => onSaveProfile(profileDraft)}
          className="mt-2 flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 transition-all text-sm"
        >
          <Save className="w-4 h-4" /> Guardar perfil
        </button>

        <h4 className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mt-5 mb-2">
          Lo que Kai ha aprendido ({(memory?.memories || []).length})
        </h4>
        <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="memories-list">
          {(memory?.memories || []).length === 0 && (
            <p className="text-xs text-slate-600 font-mono">Aún no hay recuerdos. Kai los aprenderá al hablar contigo.</p>
          )}
          {(memory?.memories || []).map((m) => (
            <div
              key={m.id}
              data-testid={`memory-${m.id}`}
              className="group flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/5"
            >
              <span className="text-sm text-slate-300">{m.text}</span>
              <button
                data-testid={`delete-memory-${m.id}`}
                onClick={() => onDeleteMemory(m.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </section>
    </DialogContent>
  </Dialog>
  );
};
